<template>
  <div class="topic-links">
    <h1>
      Locations of Interest
      <button
        v-if="userCanEdit"
        class="topic-links-add-btn"
        @click="openLoIForm(null)"
      >
        Add
      </button>
    </h1>
    <div class="u-fontsize-sm u-style-italic">
      If there are any sites on the dark or clear web that you would like us to consider adding to Arthropod,
      please <a href="/en/contact/">contact us</a>.<br>
      <strong>Note:</strong> Where NA appears in the 'Key Staff' column, this indicates that either staff information is not available for the site or not being collected at this time. The data relies on manual collection and therefore may be subject to errors or be outdated. Users are reminded to verify the information provided.
    </div>
    <div
      v-if="showSearchBar"
      class="form-group search"
    >
      <label for="search_forums">
        {{ gettext('Search locations of interest:') }}
      </label>
      <div class="input-btn-group search-with-btn">
        <div class="search-bar">
          <autocomplete-input
            inputId="search"
            :initialValue="query"
            :suggestions="loiSuggestions"
            @getSuggestions="getLoiSuggestions"
            @submitInput="searchLoi"
            @setInput="(newQuery) => query = newQuery"
          />
          <button
            ref="searchBtn"
            class="btn btn--primary btn--inline"
            @click.prevent="searchLoi"
          >
            <img
              src="/static/images/icons/icon-search-white.svg"
              width="24"
              alt="Search"
              style="display:block"
            >
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="activeFilters.length > 0"
      class="topic-links-tag-container"
    >
      Active Filters:
      <div
        v-for="(filter, i) in activeFilters"
        :key="i"
        class="topic-links-selected-tag"
      >
        <span class="u-tt-cap u-weight-bold">
          {{ filter.name }}:
        </span> {{ filter.value }}
        <button
          type="button"
          @click="removeSelectedFilter(filter)"
        >
          &#x2716;
        </button>
      </div>
    </div>
    <div
      v-if="listData.length !== 0 || showNoLoiFound"
      class="topic-links-container"
    >
      <div class="topic-links-header">
        <div class="status-cell">
          <button
            class="topic-links-sort-btn"
            :class="{ 'asc': statusDir === 'asc', 'desc': statusDir === 'desc' }"
            @click="statusSort"
          >
            Status
          </button>
          <table-filter
            v-if="filterChoices.site_status_type.length > 0"
            :displayName="filterAliases['site_status_type']"
            fieldName="site_status_type"
            :filterChoices="filterChoices.site_status_type"
            :selectedChoices="selectedFilters.site_status_type"
            @choiceSelected="newChoiceSelection"
          />
        </div>
        <div class="u-no-text-wrap crawled-cell">
          Crawled
          <table-filter
            v-if="filterChoices.has_scraped.length > 0"
            :displayName="filterAliases['has_scraped']"
            fieldName="has_scraped"
            :filterChoices="filterChoices.has_scraped"
            :selectedChoices="selectedFilters.has_scraped"
            @choiceSelected="newChoiceSelection"
          />
        </div>
        <div class="title-cell">
          Title
          <table-filter
            v-if="filterChoices.title.length > 0"
            :displayName="filterAliases['title']"
            fieldName="title"
            :filterChoices="filterChoices.title"
            :selectedChoices="selectedFilters.title"
            @choiceSelected="newChoiceSelection"
          />
        </div>
        <div class="url-cell">
          URL/Location
        </div>
        <div class="notes-cell">
          Notes
        </div>
        <div
          class="key-staff-cell"
          :class="{'width-adjust': !userCanEdit}"
        >
          Key Staff
        </div>
        <div class="last-updated-cell">
          Entry Last Updated
          <table-filter
            v-if="filterChoices.last_updated_date.length > 0"
            :displayName="filterAliases['last_updated_date']"
            fieldName="last_updated_date"
            :filterChoices="filterChoices.last_updated_date"
            :selectedChoices="selectedFilters.last_updated_date"
            @choiceSelected="newChoiceSelection"
          />
        </div>
        <div class="site-metrics topic-links-btn-col" />
        <div
          v-if="userCanEdit"
          class="topic-links-btn-col"
        />
        <div class="topic-links-btn-col" />
        <div
          v-if="userCanEdit"
          class="topic-links-btn-col"
        />
        <div
          class="tags-cell"
          :class="{'width-adjust': !userCanEdit}"
        >
          Tags
          <table-filter
            v-if="filterChoices.tags.length > 0"
            :displayName="filterAliases['tags']"
            fieldName="tags"
            :filterChoices="filterChoices.tags"
            :selectedChoices="selectedFilters.tags"
            @choiceSelected="newChoiceSelection"
          />
        </div>
        <div />
      </div>
      <div
        v-if="showNoLoiFound"
        class="alert alert--info"
      >
        <p>No Locations of Interest found! Please try:</p>
        <ul>
          <li>
            Changing your search terms
          </li>
          <li>
            Changing your filtered terms
          </li>
          <li>
            <button
              type="button"
              class="link"
              @click.prevent="clearSearchAndReset"
            >
              Clearing your search and starting over
            </button>
          </li>
        </ul>
      </div>
      <div
        v-else
        class="topic-link-items"
      >
        <div
          v-for="(item, i) in listData"
          :key="i"
          class="topic-links-item"
        >
          <div class="status-cell">
            <img
              v-if="item.site_status_type === 'D'"
              class="topic-links-updown-icon"
              src="/images/icons/topic-list-status-down.svg"
            >
            <img
              v-if="item.site_status_type === 'U'"
              class="topic-links-updown-icon"
              src="/images/icons/topic-list-status-up.svg"
            >
          </div>
          <div class="crawled-cell">
            <img
              v-if="item.has_scraped === false"
              class="topic-list-scraped-icon"
              src="/images/icons/topic-list-scraped-false.svg"
            >
            <img
              v-if="item.has_scraped === true"
              class="topic-list-scraped-icon"
              src="/images/icons/topic-list-scraped-true.svg"
            >
          </div>
          <div class="title-cell">
            <a
              v-if="item.site_index_href"
              title="Go to site index"
              :href="item.site_index_href"
            >
              {{ item.title }}
            </a>
            <span v-else>{{ item.title }}</span>
          </div>
          <div class="topic-url url-cell">
            {{ item.url }}
            <button
              class="copy-icon"
              title="Copy URL"
              @click="copyToClipboard($event, item.url)"
            />
          </div>
          <div
            class="topic-links-item-notes notes-cell"
          >
            {{ shortenNote(item.notes, 250) }}
            <button
              v-if="item.notes.length >= 250"
              class="topic-link-show-all-btn"
              @click="topicNotePopupText = item.notes"
            >
              Show All
            </button>
          </div>
          <div
            class="key-staff-cell"
            :class="{'width-adjust': !userCanEdit}"
          >
            <ul
              v-if="item.site_users.length > 0"
              class="topic-links-site-users"
            >
              <li
                v-for="(siteUser, suIndex) in sortSiteUsers(item.site_users)"
                :key="suIndex"
              >
                <a
                  class="user-detail-link"
                  title="View user details"
                  :href="`/en/user/detail/${siteUser.username}/${item.title}/`"
                  target="_blank"
                >
                  <span
                    class="title-cursor"
                    title="User's role"
                  >
                    {{ siteUser.role }}
                  </span>
                  <span
                    class="user-name"
                    title="User's Name"
                  >
                    {{ siteUser.username }}
                  </span>
                </a>
              </li>
            </ul>
            <div v-else>
              NA
            </div>
          </div>
          <div class="last-updated-cell">
            {{ item.last_updated_date_readable }}
          </div>
          <div class="site-metrics topic-links-btn-col">
            <button
                v-if="item.last_scraped_date"
                title="View Site Metrics"
                @click="viewSiteMetrics(i)"
            >
              <img
                class="topic-links-icon"
                src="/static/images/icons/analytics.svg"
                alt="metrics"
              >
            </button>
          </div>
          <div
            v-if="userCanEdit"
            class="topic-links-btn-col"
          >
            <button
              title="Delete"
              @click="confirmDelete(item.id, i)"
            >
              <img
                class="topic-links-icon"
                src="/images/icons/delete_black_48dp.svg"
              >
            </button>
          </div>
          <div class="topic-links-btn-col">
            <button
              title="View History"
              @click="viewHistory(item.id)"
            >
              <img
                class="topic-links-icon"
                src="/images/icons/history_black_48dp.svg"
              >
            </button>
          </div>
          <div
            v-if="userCanEdit"
            class="topic-links-btn-col"
          >
            <button
              title="Edit"
              @click="openLoIForm(item)"
            >
              <img
                class="topic-links-icon"
                src="/images/icons/edit_black_48dp.svg"
              >
            </button>
          </div>
          <div
            class="tags-cell"
            :class="{'width-adjust': !userCanEdit}"
          >
            <span
              v-for="(tag, j) in item.tags"
              :key="j"
            >
              <a
                class="topic-links-tag"
                href="#"
                @click.prevent="addTag(tag)"
              >
                {{ tag }}
              </a>
              <span v-if="j < item.tags.length - 1">,&nbsp;</span>
            </span>
          </div>
          <div>
            <span
              v-if="newUpdateCheck(item.last_updated_date, i)"
              class="new-location"
            >
              NEW!
            </span>
          </div>
        </div>
        <div
          v-if="copySuccess"
          ref="copySuccessMsg"
          class="url-copy-success"
          :style="{ top: copySuccessY + 'px', left: copySuccessX + 'px' }"
        >
          <p>URL copied to clipboard</p>
        </div>
      </div>
    </div>
    <div v-else>
      <p class="empty-list">
        Your list is empty.
        <span v-if="userCanEdit"> Click
          <button
            class="empty-list-btn"
            @click.prevent="openLoIForm(null)"
          >
            ADD
          </button>
          to add your first link.
        </span>
      </p>
    </div>
    <div
      v-if="showSiteMetrics"
      class="popup-overlay"
      @click.self="showSiteMetrics=false"
    >
      <div
        class="site-metrics-popup"
      >
        <button
          title="Close"
          @click="showSiteMetrics=false"
        >
          &#x2716;
        </button>
        <div class="popup-header">
          Site Metrics
        </div>
        <div class="popup-content">
          <div class="site-metric">
            <label for="total-users">Total Users:</label>
            <div id="total-users">
              <span v-if="siteMetricsItem.total_users !== null">{{ siteMetricsItem.total_users}}</span>
              <span v-else>Not Available</span>
            </div>
          </div>
          <div class="site-metric">
            <label for="total-posts">Total Posts:</label>
            <div id="toal-posts">
              <span v-if="siteMetricsItem.total_posts !== null">{{ siteMetricsItem.total_posts}}</span>
              <span v-else>Not Available</span>
            </div>
          </div>
          <div class="site-metric">
            <label for="last-scraped-date">Metrics Last Updated:</label>
            <div id="last-scraped-date">
              <span v-if="siteMetricsItem.last_scraped_date">{{ siteMetricsItem.last_scraped_date}}</span>
              <span v-else>Not Available</span>
            </div>
          </div>
        </div>
        <div class="popup-footer">
          <div class="disclaimer">
            Disclaimer: These values are based on data collected periodically and may be subject to error or be outdated. Users are reminded to verify the data.
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="show_topic_link_history"
      class="topic-links-history"
    >
      <div class="topic-links-history-close">
        <button
          title="Close"
          @click="show_topic_link_history = false"
        >
          &#x2716;
        </button>
      </div>
      <div class="topic-links-history-header">
        <div>Person</div>
        <div>Date</div>
        <div>Title</div>
        <div>Notes</div>
        <div>Url</div>
        <div>Status</div>
        <div>Crawled</div>
      </div>
      <div
        v-for="(item, dataIndex) in history_data"
        :key="dataIndex"
        class="topic-links-history-item"
      >
        <div>{{ item.user_info }}</div>
        <div>{{ item.update_date }}</div>
        <div>{{ item.all_titles }}</div>
        <div>
          {{ shortenNote(item.all_notes, 100) }}
          <button
            v-if="item.all_notes.length >= 100"
            class="topic-link-show-all-btn"
            @click="topicNotePopupText = item.all_notes"
          >
            Show All
          </button>
        </div>
        <div>{{ item.all_urls }}</div>
        <div>
          <img
            v-if="item.site_status === 'D'"
            class="topic-links-updown-icon"
            src="/images/icons/topic-list-status-down.svg"
          >
          <img
            v-if="item.site_status === 'U'"
            class="topic-links-updown-icon"
            src="/images/icons/topic-list-status-up.svg"
          >
        </div>
        <div>
          <img
            v-if="item.has_scraped === false"
            class="topic-list-scraped-icon"
            src="/images/icons/topic-list-scraped-false.svg"
          >
          <img
            v-if="item.has_scraped === true"
            class="topic-list-scraped-icon"
            src="/images/icons/topic-list-scraped-true.svg"
          >
        </div>
      </div>
      <p>
        <button
          class="topic-links-history-btn"
          type="button"
          @click="show_topic_link_history = false"
        >
          Close
        </button>
      </p>
    </div>
    <div
      v-if="showConfirmDelete && userCanEdit"
      class="topic-links-confirm-delete"
    >
      <p>Confirm deletion of <strong>{{ deleteInfo.url }}</strong></p>
      <p class="buttons">
        <button
          class="cancel-btn"
          @click="clearDelete"
        >
          Cancel
        </button>
        <button
          class="confirm-btn"
          @click="deleteTopicList"
        >
          Confirm
        </button>
      </p>
    </div>
    <div
      v-if="topicNotePopupText"
      class="topic-links-note-popup-container"
      @click.self="topicNotePopupText = ''"
    >
      <div class="topic-links-note-popup">
        <button
          class="all-notes-full-note-popup-close"
          @click="topicNotePopupText = ''"
        >
          &#x2716;
        </button>
        {{ topicNotePopupText }}
      </div>
    </div>

    <div
      v-if="loiFormOpen"
      class="topic-link-popup-form"
      @click.self="loiFormOpen = false"
    >
      <location-of-interest-form
        :existingUrls="existingUrls"
        :formAction="loiFormAction"
        :fullTagList="fullTagList"
        :initialData="loiFormItem"
        @formCancel="loiFormOpen = false"
        @formSubmit="submitLoiForm"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import {getMonthName, getRolePrecedence} from './utils'
import AutocompleteInput from './AutocompleteInput'
import LocationOfInterestForm from './forms/LocationOfInterestForm'
import TableFilter from './TableFilter'

export default {
  name: 'TopicList',
  components: {
    AutocompleteInput,
    LocationOfInterestForm,
    TableFilter
  },
  data() {
    return {
      fullList: [],
      listData: [],
      history_data: [],
      history_exists: false,
      topic_link_history: [],
      search_results: [],
      show_topic_link_history: false,
      update_notes: false,
      index: '',
      copySuccess: false,
      copySuccessX: null,
      copySuccessY: null,
      currentIndex: null,
      existingUrls: [],
      selectedTags: [],
      fullTagList: [],
      showConfirmDelete: false,
      deleteInfo: {
        url: null,
        id: null
      },
      topicNotePopupText: '',
      sortDir: {'desc': 1, 'asc': -1},
      statusDir: null,
      userCanEdit: false,
      loiFormAction: 'create',
      loiFormOpen: false,
      loiFormItem: null,
      loiSuggestions: [],
      query: '',
      showNoLoiFound: false,
      showSearchBar: false,
      siteMetricsItem: null,
      showSiteMetrics: false,
      activeFilters: [],
      filterChoices: {},
      selectedFilters: {},
      filterAliases: {
        site_status_type: 'status',
        has_scraped: 'crawled',
        title: 'title',
        last_updated_date: 'last updated',
        tags: 'tags',
      },
    }
  },
  computed: {
    filtersQueryString() {
      let filters = []
      for (const [filterName, filterValues] of Object.entries(this.selectedFilters)) {
        filterValues.forEach(value => filters.push(filterName + ':' + value))
      }
      return filters.join(',')
    },
  },
  mounted() {
    this.query = new URLSearchParams(window.location.search).get('query')
    this.getUpdatedTopicList()

    window.addEventListener('popstate', (event) => {
      this.query = new URLSearchParams(window.location.search).get('query')
      this.getUpdatedTopicList()
    })
  },
  methods: {
    addTag(tag) {
      if (this.selectedFilters.tags.indexOf(tag) < 0) {
        this.selectedFilters.tags.push(tag)
        this.applyFilters()
        let url = new URL(window.location)
        url.searchParams.set('filters', this.filtersQueryString)
        window.history.pushState({}, '', url)
      }
    },
    applyFilters() {
      this.listData = this.fullList
      for (const [filterName, filterValues] of Object.entries(this.selectedFilters)) {
        if (filterValues != null && filterValues.length > 0) {
          this.listData = this.listData.filter(item => {
            let hasMatched = false
            for (let i = 0; i < filterValues.length && !hasMatched; i++) {
              let filterValue = filterValues[i]
              if (filterName === 'tags') {
                hasMatched = item[filterName].indexOf(filterValue) >= 0
              } else if (filterName === 'last_updated_date') {
                hasMatched = this.getFormattedDate(item[filterName]) === filterValue
              } else if (typeof item[filterName] === 'string') {
                hasMatched = item[filterName] === filterValue
              } else {
                hasMatched = item[filterName].toString() === filterValue
              }
            }
            return hasMatched
          })
        }
      }
      this.showNoLoiFound = (this.filtersQueryString.length > 0 || this.query !== '') && this.listData.length === 0
      this.showSearchBar = this.query !== '' || this.fullList.length > 0
      this.setActiveFilters()
    },
    clearSearchAndReset() {
      this.query = ''
      this.$nextTick(() => this.searchLoi())
    },
    clearFilterChoices() {
      this.filterChoices = {
        site_status_type: [{display: 'Up', value: 'U'}, {display: 'Down', value: 'D'}],
        has_scraped: [{display: 'Yes', value: 'true'}, {display: 'No', value: 'false'}],
        title: [],
        last_updated_date: [],
        tags: [],
      }
    },
    clearSelectedFilters() {
      this.selectedFilters = {
        site_status_type: [],
        has_scraped: [],
        title: [],
        last_updated_date: [],
        tags: [],
      }
    },
    getLoiSuggestions(term) {
      this.query = term
      let params = {'term': term}
      axios.get('/en/autocomplete/loi', {params})
        .then((response) => {
          this.loiSuggestions = response.data
        })
    },
    openLoIForm(loiItem) {
      this.loiFormItem = loiItem
      this.loiFormAction = loiItem != null ? 'update' : 'create'
      this.loiFormOpen = true
    },
    searchLoi() {
      let url = new URL(window.location)
      url.searchParams.set('query', this.query)
      url.searchParams.delete('filters')
      window.history.pushState({}, '', url)
      this.getUpdatedTopicList()
    },
    sortSiteUsers(siteUsers) {
      return siteUsers.toSorted((a, b) => {
        return getRolePrecedence(b.role) - getRolePrecedence(a.role)
      })
    },
    submitLoiForm() {
      this.getUpdatedTopicList()
      this.loiFormItem = null
      this.loiFormAction = 'create'
      this.loiFormOpen = false
    },
    copyToClipboard(e, url) {
      this.copySuccess = true
      this.copySuccessX = e.pageX + 20
      this.copySuccessY = e.pageY + 20
      navigator.clipboard.writeText(url)
      this.$nextTick(() => {
        this.$refs.copySuccessMsg.addEventListener('animationend', () => {
          this.copySuccess = false
        })
      })
    },
    confirmDelete(id, index) {
      this.deleteInfo.url = this.fullList[index].url
      this.deleteInfo.id = id
      this.showConfirmDelete = true
    },
    clearDelete() {
      this.deleteInfo.url = null
      this.deleteInfo.id = null
      this.showConfirmDelete = false
    },
    convertDate(date) {
      let splitDate = date.split(' ')
      let splitDMY = splitDate[0].split('/')
      let newDate = splitDMY[2] + '-' + splitDMY[1] + '-' + splitDMY[0] + 'T' + splitDate[1]
      return Date.parse(newDate)
    },
    deleteTopicList() {
      const csrftoken = Cookies.get('csrftoken')
      axios.post('/en/topic/delete/', this.deleteInfo.id, {'headers': {'X-CSRFToken': csrftoken}}).then(response => {
        if (response.status === 200) {
          this.getUpdatedTopicList()
          this.clearDelete()
        } else {
          console.log('Something went wrong')
          this.showConfirmDelete = false
        }
      }).catch((error) => {
        console.log(error)
        this.showConfirmDelete = false
      })
    },
    getFormattedDate(dateString) {
      let date = new Date(dateString)
      return getMonthName(date.getMonth()) + ' ' + date.getFullYear()
    },
    getUpdatedTopicList() {
      const csrftoken = Cookies.get('csrftoken')
      axios.get('/en/topic/list/', {'headers': {'X-CSRFToken': csrftoken}, 'params': {'query': this.query}}).then(response => {
        if (response.status === 200) {
          this.fullList = this.listData = response.data.list
          this.fullTagList = response.data.tags
          this.userCanEdit = response.data.user_can_edit
          this.existingUrls = []
          this.clearFilterChoices()
          this.fullList.forEach((item) => {
            this.existingUrls.push(item.url)
            this.filterChoices.title.push({display: item.title, value: item.title})
            let formattedLastUpdateDate = this.getFormattedDate(item.last_updated_date)
            this.filterChoices.last_updated_date.push({
              display: formattedLastUpdateDate,
              value: formattedLastUpdateDate,
            })
            item.tags.forEach(tag => { this.filterChoices.tags.push({display: tag, value: tag}) })
          })
          this.filterChoices.title.sort((a, b) => a.value > b.value)
          this.filterChoices.last_updated_date.sort((a, b) => new Date(a.value) > new Date(b.value))
          this.filterChoices.tags.sort((a, b) => a.value > b.value)
          this.parseFiltersFromURL()
          this.applyFilters()
        }
      }).catch((error) => {
        console.log(error)
      })
    },
    newChoiceSelection(fieldName, elementId, choice) {
      if (document.getElementById(elementId)?.checked === true) {
        this.selectedFilters[fieldName].push(choice)
      } else {
        this.selectedFilters[fieldName] = this.selectedFilters[fieldName]
          .filter(filterValue => filterValue !== choice)
      }
      this.applyFilters()
      let url = new URL(window.location)
      url.searchParams.set('filters', this.filtersQueryString)
      window.history.pushState({}, '', url)
    },
    parseFiltersFromURL() {
      this.clearSelectedFilters()
      let url = new URL(window.location)
      let filters = url.searchParams.get('filters')
      if (filters) {
        filters.split(',').forEach((filter) => {
          let splitFilter = filter.split(':', 2)
          this.selectedFilters[splitFilter[0]].push(splitFilter[1])
        })
      }
    },
    removeSelectedFilter(filter) {
      let name = ''
      for (const [filterName, filterAlias] of Object.entries(this.filterAliases)) {
        if (filterAlias === filter.name) {
          name = filterName
          break
        }
      }
      this.selectedFilters[name] = this.selectedFilters[name].filter(
        (filterValue) => {
          let value = filter.value
          if (name === 'site_status_type') {
            value = value === 'Up' ? 'U' : 'D'
          } else if (name === 'has_scraped') {
            value = value === 'Yes' ? 'true' : 'false'
          }
          return filterValue !== value
        }
      )
      this.applyFilters()
      let url = new URL(window.location)
      url.searchParams.set('filters', this.filtersQueryString)
      window.history.pushState({}, '', url)
    },
    setActiveFilters() {
      this.activeFilters = []
      for (const [filterName, filterValues] of Object.entries(this.selectedFilters)) {
        filterValues.forEach(value => {
          if (filterName === 'site_status_type') {
            value = value === 'U' ? 'Up' : 'Down'
          } else if (filterName === 'has_scraped') {
            value = value === 'true' ? 'Yes' : 'No'
          }
          this.activeFilters.push({name: this.filterAliases[filterName], value: value})
        })
      }
    },
    viewHistory(id) {
      this.show_topic_link_history = true
      for (let index = 0; index < this.listData.length; index++) {
        if (this.listData[index].id === id) {
          this.history_data = this.listData[index].history
        }
      }
      this.history_data.sort((a, b) => this.convertDate(b.update_date) - this.convertDate(a.update_date))
    },
    viewSiteMetrics(index) {
      this.showSiteMetrics = true
      this.siteMetricsItem = this.listData[index]
    },
    shortenNote(note, noteLen) {
      if (note.length >= noteLen) {
        return note.substring(0, noteLen) + '...'
      } else {
        return note
      }
    },
    statusSort() {
      this.statusDir = (this.statusDir === 'asc') ? 'desc' : 'asc'
      this.listData.sort((a, b) => this.sortDir[this.statusDir] * a.site_status_type.localeCompare(b.site_status_type))
    },
    newUpdateCheck(time, index) {
      let lastVisit = parseInt(Cookies.get('last-visit'))
      let postTime = new Date(time).getTime()
      if (isNaN(lastVisit) || lastVisit < postTime) {
        if (index === this.listData.length - 1) this.setLastVisit()
        return true
      } else {
        if (index === this.listData.length - 1) this.setLastVisit()
        return false
      }
    },
    setLastVisit() {
      let date = new Date()
      Cookies.set('last-visit', date.setDate(date.getDate()))
    }
  }

}
</script>

<style>
.topic-links {
  width: 96vw;
  margin: auto;
  max-width: 1700px;
}

.topic-links-add-btn {
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: #a42334;
  color: #fff;
  text-decoration: none;
  padding: 10px;
  border-radius: 6px;
  position: relative;
  top: -10px;
  left: 30px;
}

.topic-links-header,
.topic-links-item,
.topic-links-history-header,
.topic-links-history-item {
  display: flex;
  width: 100%;
}

.topic-links-container {
  overflow-x: scroll;
}

.topic-links-header,
.topic-links-item {
  flex-direction: row;
  justify-content: flex-start;
  min-width: 1600px;
}
.topic-links-header > div,
.topic-links-item > div {
  padding: 0.35rem 0.4rem;
  font-size: 0.85em;
  word-break: break-word;
  flex-grow: 1;
}

.topic-links-history-header > div,
.topic-links-history-item > div {
  flex-grow: 1;
  font-size: 0.85em;
  padding: 0.35rem 0.5rem;
  width: 25%
}

.topic-links-history-item:nth-child(odd) {
  background: #f5f5f4;
}

.topic-links-header {
  border-bottom: 2px solid #ccc;
  font-weight: bold;
}
.topic-links-header > div {
  display: flex;
  align-items: center;
}

.topic-links-item > div:nth-child(10) {
  word-break: break-all;
}

.status-cell,
.crawled-cell {
  min-width: 5rem;
  width: 5rem;
}
.title-cell {
  min-width: 8rem;
  width: 8rem;
}
.url-cell {
  min-width: 17rem;
  max-width: 17rem;
}
.notes-cell {
  min-width: 12rem;
  width: 12rem;
  word-break: break-word;
}
.key-staff-cell {
  min-width: 9rem;
  width: 9rem;
}
.key-staff-cell.width-adjust {
  min-width: 12rem;
  width: 12rem;
}
.last-updated-cell {
  min-width: 6.1rem;
  width: 6.1rem;
  word-break: break-word;
}
.tags-cell {
  min-width: 6.25rem;
  width: 6.25rem;
}
.tags-cell.width-adjust {
  min-width: 7rem;
  width: 7rem;
}
.topic-links-btn-col {
  min-width: 2.75rem;
  width: 2.75rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
}
.topic-links-header > div:last-child,
.topic-links-item > div:last-child {
  max-width: 3rem;
  width: 3rem;
}

.topic-links-item {
  padding: 0.25rem 0;
}
.topic-links-item > div {
  max-height: 9rem;
  overflow-y: auto;
}

.topic-links-item:nth-child(even) {
  background: #f5f5f4;
}

.topic-links-icon {
  max-width: 35px;
  max-height: 35px;
}

.topic-links-copy {
  max-width: 24px;
  max-height: 24px;
  margin-left: 4px;
}

.url-copy-success {
  position: absolute;
  background: #e2ce66;
  border: 1px solid #000;
  padding: 10px 20px;
  animation-name: fadeOutSuccess;
  animation-duration: 1s;
  animation-delay: 3s;
}

.url-copy-success p {
  margin-bottom: 0;
}

@keyframes fadeOutSuccess {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.topic-links-add-url,
.topic-links-url-notes,
.topic-links-history,
.topic-links-url-exists,
.topic-links-confirm-delete,
.site-metrics-popup
{
  position: fixed;
  width: 50vw;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #ccc;
  background: #fff;
  padding: 30px;
  box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.2);
  z-index: 100;
}
.topic-link-popup-form,
.topic-links-update-notes,
.popup-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  background: rgba(0,0,0,0.2);
  display: flex;
}

.topic-links-history {
  max-height: 70vh;
  overflow-y: scroll;
}

.topic-links-update-notes label,
.topic-links-url-notes label {
  margin: 10px 0;
}

.topic-links-update-notes label:first-of-type,
.topic-links-url-notes label:first-of-type {
  margin-top: 0;
}

.topic-links-add-url p,
.topic-links-url-notes p.buttons,
.topic-links-update-notes p.buttons,
.topic-links-history p,
.topic-links-url-exists p.buttons,
.topic-links-confirm-delete p.buttons {
  text-align: right;
  margin-top: 20px;
  margin-bottom: 0;
}

.topic-links-add-url p.error {
  text-align: left;
  color: #a42334;
  position: absolute;
}

.topic-links-add-url .add-btn,
.topic-links-url-notes .add-btn,
.topic-links-update-notes .update-btn,
.topic-links-url-exists .update-btn,
.topic-links-confirm-delete .confirm-btn {
  background-color: #a42334;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
}

.topic-links-add-url .cancel-btn,
.topic-links-url-notes .cancel-btn,
.topic-links-update-notes .cancel-btn,
.topic-links-history-btn,
.topic-links-url-exists .cancel-btn,
.topic-links-confirm-delete .cancel-btn {
  background-color: #fff;
  color: #a42334;
  border: 1px solid #a42334;
  padding: 10px;
  border-radius: 6px;
}

.topic-links-history-close {
  position: absolute;
  top: 5px;
  right: 15px;
}

.topic-links-tag {
  text-decoration: none;
}

.topic-links-tag:hover {
  text-decoration: underline;
}

.topic-links-tag-container {
  margin-bottom: 20px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
}

.topic-links-selected-tag {
  background-color: #a42334;
  color: #fff;
  padding: 5px;
  border-radius: 6px;
  font-size: 0.85em;
}

.topic-links-tag-container .topic-links-selected-tag:first-of-type {
  margin-left: 10px;
}

.empty-list {
  text-align: center;
  margin-top: 100px;
}

.empty-list-btn {
  background-color: #a42334;
  color: #fff;
  padding: 5px;
  border-radius: 6px;
  font-size: 0.85em;
}

.topic-links-update-notes textarea,
.topic-links-url-notes textarea {
  height: 200px;
  resize: none;
}

.topic-links-item-notes {
  word-break: break-word;
}

.topic-link-show-all-btn {
  color: #c22c3f;
  float: right;
}

.topic-links-note-popup-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 101;
  background: rgba(0, 0, 0, 0.2);
}

.topic-links-note-popup {
  position: fixed;
  top: 50%;
  z-index: 11;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  width: 60vw;
  max-height: 60vh;
  background: #f5f5f4;
  padding: 20px 30px;
  border: 2px solid #cecbca;
  border-radius: 6px;
  box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.2);
  white-space: pre-line;
  overflow: scroll;
}

.topic-links-updown-icon {
  width: 32px;
}
.topic-list-scraped-icon {
  width: 32px;
}
.topic-links-sort-btn {
  color: #a42334;
  text-wrap: nowrap;
  padding: 0;
}
.desc:after {
  content: '▼';
  display: inline-block;
  color: black;
  text-decoration: none;
}
.asc:after {
  content: '▲';
  display: inline-block;
  color: black;
  text-decoration: none;
}
.status-scraped {
  display: flex;
  margin-top: 10px;
  justify-content: space-evenly;
}
.status-scraped > div {
  width:45%;
}
span[title].title-cursor,
span[title].user-name {
  cursor: pointer;
}
.title-cursor {
  background: #a42334;
  color: #fff;
  padding: 2px 4px;
  border-radius: 4px;
  text-align: center;
  width: 100%;
  display: inline-block;
}
.topic-links-site-users {
  margin: 0;
  li::marker {
    content: '';
  }
  li {
    word-break: break-all;
    padding: 10px;
    border: 1px solid #ccc;
    margin-bottom: 7px;
  }
}
.topic-url {
  display: flex;
  flex-direction: row;
  gap: 3px;
  justify-content: space-between;
  word-break: break-all;
}
.link-icon,
.copy-icon {
  background-position: center;
  background-repeat: no-repeat;
  height: 24px;
  min-width: 24px;
}
.link-icon {
  margin-left: 4px;
  background-image: url('/static/images/icons/icon-link.svg');
}
.copy-icon {
  background-image: url('/static/images/icons/content_copy.svg');
}
.user-detail-link {
  color: #000;
  text-decoration: none;
}
.user-name {
  display: block;
  width: 100%;
  text-align: center;
}

.search-with-btn button {
  vertical-align: top;
  padding-top: 0.4em;
  padding-bottom: 0.4em;
  border: 2px solid #c22c3f;
}
.search-with-btn input {
  width: 100%;
}
.search-bar {
  display: flex;
  justify-items: stretch;
  gap: 5px;
  .autocomplete {
    width: calc(100% - 78px);
  }
}
.search {
  margin-top: 1.25rem;
}
.new-location {
  background: #a42334;
  color: #fff;
  border-radius: 5px;
  font-size: 14px;
  padding: 3px;
}
.new-update {
  color: red;
}
.site-metrics-popup > button {
  position: absolute;
  top: 10px;
  right: 10px;
}
.popup-header {
  font-size: x-large;
  font-weight: bold;
  padding: 0;
  margin-bottom: 0.25rem;
  border-bottom: 2px solid lightgrey;
}
.popup-content label {
  width: 30%;
}
.site-metric {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
}
.popup-footer {
  margin-top: 10px;
}
</style>
